import {setTitle} from '@github-ui/document-metadata'
import {hpcAttrs} from '@github-ui/hpc'
import {JobInfoWithSubscription} from '@github-ui/issues-bulk-actions/JobInfoWithSubscription'
import {LABELS} from '@github-ui/issues-shared/labels'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useQuery} from '@github-ui/react-query'
import {SoftNavOnlyLink as Link, useLocation, useParams} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {getIconComponent, SAVED_VIEW_OPEN_SOURCES, type SavedViewOpenLocationState} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {useUser} from '@github-ui/use-user'
import {AlertIcon, BookmarkSlashIcon, PinIcon} from '@primer/octicons-react'
import {Button, Link as PrimerLink} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {renderIssuesSidebar} from '../../components/sidebar/render-issues-sidebar'
import {SavedViewIssueList} from '../../components/views/SavedViewIssueList'
import {SavedViewMutationErrorBanner} from '../../components/views/SavedViewMutationErrorBanner'
import {ViewsBackLink} from '../../components/views/ViewsBackLink'
import {BUTTON_LABELS} from '../../constants/buttons'
import {PreviewQueryProvider} from '../../contexts/PreviewQueryContext'
import {useQueryBulk} from '../../contexts/QueryContext'
import {useConfirmDiscardChanges} from '../../hooks/use-confirm-discard-changes'
import {
  SAVED_VIEW_PIN_SOURCES,
  SAVED_VIEW_UPDATE_SOURCES,
  useSavedViewAnalytics,
} from '../../hooks/use-saved-view-analytics'
import {useSavedViewPinControls} from '../../hooks/use-saved-view-pin-controls'
import {useUpdateSavedViewMutation} from '../../hooks/use-update-saved-view-mutation'
import {IssueRepoNewChoosePage, IssueRepoNewPage, IssueShowPage, IssueViewsPage} from '../../lazy-routes'
import {SavedViewFetchError, savedViewQueryOptions} from '../../queries/saved-views'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import styles from './RepositorySavedViewPage.module.css'
import {SavedViewActions} from './SavedViewActions'
import {SavedViewEditView} from './SavedViewEditView'
import {SavedViewFilterBar} from './SavedViewFilterBar'
import {SavedViewHeader, ViewHeaderIcon} from './SavedViewHeader'

function RepositorySavedViewContent() {
  const {owner = '', repo = '', id = ''} = useParams<{owner: string; repo: string; id: string}>()
  const viewsPath = `/${owner}/${repo}/issues/views`
  const {pathname, search, state} = useLocation()
  const navigate = useNavigate()

  const {data, error, isError, isLoading, refetch} = useQuery(savedViewQueryOptions({owner, repo, id}))

  const savedView = data?.savedView
  const viewerCanManageViews = data?.viewerCanManageViews ?? false
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')

  const {trackViewOpened, trackEditOpened, trackViewUpdated} = useSavedViewAnalytics()
  const {currentUser} = useUser()
  const openSource = (state as SavedViewOpenLocationState | null)?.savedViewOpenSource ?? SAVED_VIEW_OPEN_SOURCES.direct
  const trackedOpenIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (savedView === undefined) return
    if (trackedOpenIdRef.current === savedView.id) return
    trackedOpenIdRef.current = savedView.id
    const openedByCreator = currentUser?.login != null && currentUser.login === savedView.creatorLogin
    trackViewOpened(savedView.id, openSource, openedByCreator)
  }, [savedView, trackViewOpened, openSource, currentUser])

  useEffect(() => {
    if (savedView?.name) {
      setTitle(LABELS.documentTitleForView(savedView.name))
    }
  }, [savedView?.name])

  const urlQuery = useMemo(() => new URLSearchParams(search).get('q'), [search])
  const effectiveQuery = urlQuery ?? savedView?.query ?? ''

  const updateUrlQuery = useCallback(
    (nextQuery: string) => {
      const trimmed = nextQuery.trim()
      const params = new URLSearchParams(search)
      if (trimmed) {
        params.set('q', trimmed)
      } else {
        params.delete('q')
      }
      // Reset pagination when the query changes; the new result set may be shorter.
      params.delete('page')
      const query = params.toString()
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [navigate, pathname, search],
  )

  const [isEditing, setIsEditing] = useState(false)

  const handleStartEditing = useCallback(() => {
    if (savedView !== undefined) {
      trackEditOpened(savedView.id)
    }
    setIsEditing(true)
  }, [savedView, trackEditOpened])

  const handleExitEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleSaveSuccess = useCallback(() => {
    if (urlQuery !== null) {
      updateUrlQuery('')
    }
    setIsEditing(false)
  }, [updateUrlQuery, urlQuery])

  const {mutate: updateView, isPending: isUpdating, error: updateError} = useUpdateSavedViewMutation({owner, repo})

  const {pinControls, pinErrorMessage, retryPin, isPinRetrying} = useSavedViewPinControls({
    owner,
    repo,
    enabled: pinnedViewsEnabled,
    source: SAVED_VIEW_PIN_SOURCES.individualViewActionsMenu,
  })

  const handleSaveChanges = useCallback(() => {
    if (!savedView) return
    const trimmedQuery = effectiveQuery.trim()
    updateView(
      {
        id: savedView.id,
        name: savedView.name,
        query: trimmedQuery,
        description: savedView.description ?? undefined,
        icon: savedView.icon,
        color: savedView.color,
      },
      {
        onSuccess: () => {
          trackViewUpdated(savedView.id, SAVED_VIEW_UPDATE_SOURCES.query)
          updateUrlQuery('')
        },
      },
    )
  }, [effectiveQuery, savedView, trackViewUpdated, updateUrlQuery, updateView])

  const readPreviewQueryValue = useMemo(
    () => ({currentQuery: effectiveQuery, onQueryChange: updateUrlQuery}),
    [effectiveQuery, updateUrlQuery],
  )

  // The query is "dirty" when a triage+ viewer has edited it away from the saved
  // value but hasn't saved yet. Read-only viewers can't save, so they have
  // nothing to discard.
  const isQueryDirty = viewerCanManageViews && savedView != null && effectiveQuery.trim() !== savedView.query.trim()
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty: isQueryDirty})

  const handleBackLinkClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    event => {
      if (event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (!isQueryDirty) return
      event.preventDefault()
      void (async () => {
        if (await confirmDiscardIfDirty()) {
          navigate(viewsPath)
        }
      })()
    },
    [isQueryDirty, confirmDiscardIfDirty, navigate, viewsPath],
  )

  const isNotFound = error instanceof SavedViewFetchError && error.status === 404
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const liftHeader = sidebarLayoutEnabled && savedView != null && !isEditing && !isError

  const notFoundBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <BookmarkSlashIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.notFoundTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.notFoundDescription}</Blankslate.Description>
      <Button as={Link} to={viewsPath} variant="primary" className={styles.blankslateAction}>
        {LABELS.views.backToViews}
      </Button>
    </Blankslate>
  )

  const errorBlankslate = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.loadErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.loadErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => refetch()}>{BUTTON_LABELS.tryAgain}</Blankslate.PrimaryAction>
      <PrimerLink as={Link} to={viewsPath}>
        {LABELS.views.backToViews}
      </PrimerLink>
    </Blankslate>
  )

  const fetchErrorBlankslate = isNotFound ? notFoundBlankslate : errorBlankslate

  const renderErrorFallback = (
    <Blankslate spacious>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{LABELS.views.displayErrorTitle}</Blankslate.Heading>
      <Blankslate.Description>{LABELS.views.displayErrorDescription}</Blankslate.Description>
      <Blankslate.PrimaryAction onClick={() => window.location.reload()}>
        {BUTTON_LABELS.reloadPage}
      </Blankslate.PrimaryAction>
    </Blankslate>
  )

  const headerTitle =
    liftHeader && savedView ? (
      <span className={styles.headerTitle}>
        <ViewHeaderIcon icon={getIconComponent(savedView.icon)} color={savedView.color} />
        {savedView.name}
        {pinnedViewsEnabled && savedView.pinned && <PinIcon size={16} />}
      </span>
    ) : undefined

  const headerActions =
    liftHeader && savedView ? (
      <SavedViewActions
        savedView={savedView}
        owner={owner}
        repo={repo}
        viewsPath={viewsPath}
        onStartEditing={handleStartEditing}
        canManage={viewerCanManageViews}
        pinControls={pinControls}
      />
    ) : undefined

  const contextArea = liftHeader ? <ViewsBackLink viewsPath={viewsPath} onClick={handleBackLinkClick} /> : undefined

  return (
    <RepoIssuesPageLayout
      dialogTitle={LABELS.viewsTitle}
      title={headerTitle}
      contextArea={contextArea}
      actions={headerActions}
      renderSidebar={renderIssuesSidebar}
    >
      <div className={sidebarLayoutEnabled ? undefined : 'mt-3'} {...hpcAttrs()} aria-busy={isLoading}>
        <ErrorBoundary boundaryName="RepositorySavedViewPage" fallback={renderErrorFallback}>
          {isError ? (
            fetchErrorBlankslate
          ) : savedView ? (
            isEditing ? (
              <SavedViewEditView
                key={savedView.id}
                savedView={savedView}
                owner={owner}
                repo={repo}
                onSaved={handleSaveSuccess}
                onCancel={handleExitEditing}
              />
            ) : (
              <>
                {liftHeader ? (
                  savedView.description && <p className={styles.description}>{savedView.description}</p>
                ) : (
                  <SavedViewHeader
                    savedView={savedView}
                    owner={owner}
                    repo={repo}
                    viewsPath={viewsPath}
                    onStartEditing={handleStartEditing}
                    canManage={viewerCanManageViews}
                    pinnedViewsEnabled={pinnedViewsEnabled}
                    pinControls={pinControls}
                  />
                )}
                <div className={styles.filterBarWrapper}>
                  <SavedViewMutationErrorBanner
                    message={pinErrorMessage}
                    onRetry={retryPin}
                    isRetrying={isPinRetrying}
                    focusOnMount={false}
                  />
                  <SavedViewMutationErrorBanner
                    message={updateError ? LABELS.views.updateError : null}
                    onRetry={handleSaveChanges}
                    isRetrying={isUpdating}
                  />
                  <SavedViewFilterBar
                    owner={owner}
                    repo={repo}
                    savedView={savedView}
                    currentQuery={effectiveQuery}
                    onQueryChange={updateUrlQuery}
                    onSaveChanges={handleSaveChanges}
                    isSaving={isUpdating}
                    canManage={viewerCanManageViews}
                  />
                </div>
                <div className={styles.middlePaneGrid}>
                  <PreviewQueryProvider value={readPreviewQueryValue}>
                    <SavedViewIssueList owner={owner} repo={repo} query={effectiveQuery} preheatEnabled />
                  </PreviewQueryProvider>
                </div>
              </>
            )
          ) : null}
        </ErrorBoundary>
      </div>
    </RepoIssuesPageLayout>
  )
}

export function RepositorySavedViewPage() {
  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueViewsPage.preload()
      IssueShowPage.preload()
      IssueRepoNewPage.preload()
      IssueRepoNewChoosePage.preload()
    })
  }, [])

  const {bulkJobId, setBulkJobId, triggerBulkJobSuccess} = useQueryBulk()

  return (
    <AnalyticsWrapper category="Repository Saved View">
      <JobInfoWithSubscription bulkJobId={bulkJobId} setBulkJobId={setBulkJobId} onSuccess={triggerBulkJobSuccess}>
        <RepositorySavedViewContent />
      </JobInfoWithSubscription>
    </AnalyticsWrapper>
  )
}
